import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-zinc-800 rounded-lg">
      <div className="px-4 py-3 border-b border-zinc-800">
        <h3 className="text-sm font-medium text-zinc-200">{title}</h3>
      </div>
      <div className="px-4 py-4 space-y-4">{children}</div>
    </div>
  );
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}

export function Input({
  value,
  onChange,
  placeholder,
  mono,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-indigo-500 ${mono ? "font-mono" : ""}`}
    />
  );
}

export function SaveButton({ dirty, saving, onClick }: { dirty: boolean; saving: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={!dirty || saving}
      className="px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-indigo-600 hover:bg-indigo-500 text-white"
    >
      {saving ? "Saving..." : "Save changes"}
    </button>
  );
}

export function ProjectSettings({ projectId }: { projectId: Id<"projects"> }) {
  const projects = useQuery(api.projects.list, {});
  const repositories = useQuery(api.repositories.list, {});
  const project = projects?.find((p) => p._id === projectId);
  const repo = repositories?.find((r) => r._id === project?.repositoryId);

  if (!project || !repositories) {
    return <div className="text-zinc-600 text-sm py-4">Loading...</div>;
  }

  return (
    <div className="max-w-2xl space-y-6">
      <ProjectSection project={project} repo={repo} />
      <DangerSection projectId={projectId} projectName={project.name} />
    </div>
  );
}

function ProjectSection({
  project,
  repo,
}: {
  project: { _id: Id<"projects">; name: string; repoPath: string; repositoryId?: Id<"repositories"> };
  repo?: { _id: Id<"repositories">; name: string; slug: string; repoPath: string };
}) {
  const updateProject = useMutation(api.projects.update);
  const [name, setName] = useState(project.name);
  const [saving, setSaving] = useState(false);

  const dirty = name !== project.name;

  const save = async () => {
    setSaving(true);
    try {
      await updateProject({
        projectId: project._id,
        ...(name !== project.name ? { name } : {}),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="PRD Execution">
      <Field label="Name">
        <Input value={name} onChange={setName} placeholder="PRD execution name" />
      </Field>
      {repo && (
        <Field label="Project">
          <a
            href={`#/${repo.slug}/settings`}
            className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
          >
            {repo.name} ({repo.slug})
          </a>
        </Field>
      )}
      <Field label="Repo Path">
        <span className="text-sm text-zinc-400 font-mono">{project.repoPath || "—"}</span>
      </Field>
      <div className="flex justify-end">
        <SaveButton dirty={dirty} saving={saving} onClick={() => void save()} />
      </div>
    </Section>
  );
}

function RepositorySection({
  repo,
}: {
  repo: { _id: Id<"repositories">; name: string; slug: string; repoPath: string };
}) {
  const updateRepo = useMutation(api.repositories.update);
  const [name, setName] = useState(repo.name);
  const [repoPath, setRepoPath] = useState(repo.repoPath);
  const [saving, setSaving] = useState(false);

  const dirty = name !== repo.name || repoPath !== repo.repoPath;

  const save = async () => {
    setSaving(true);
    try {
      await updateRepo({
        repositoryId: repo._id,
        ...(name !== repo.name ? { name } : {}),
        ...(repoPath !== repo.repoPath ? { repoPath } : {}),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Repository">
      <Field label="Name">
        <Input value={name} onChange={setName} placeholder="Repository name" />
      </Field>
      <Field label="Slug">
        <span className="text-sm text-zinc-400 font-mono">{repo.slug}</span>
        <span className="text-[11px] text-zinc-600">Auto-derived from name on save</span>
      </Field>
      <Field label="Repo Path">
        <Input value={repoPath} onChange={setRepoPath} placeholder="/path/to/repo" mono />
      </Field>
      <div className="flex justify-end">
        <SaveButton dirty={dirty} saving={saving} onClick={() => void save()} />
      </div>
    </Section>
  );
}

function DangerSection({
  projectId,
  projectName,
}: {
  projectId: Id<"projects">;
  projectName: string;
}) {
  const removeProject = useMutation(api.projects.remove);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await removeProject({ projectId });
      window.location.hash = "/";
      window.location.reload();
    } catch {
      setDeleting(false);
    }
  };

  return (
    <div className="border border-red-500/20 rounded-lg">
      <div className="px-4 py-3 border-b border-red-500/20">
        <h3 className="text-sm font-medium text-red-400">Danger Zone</h3>
      </div>
      <div className="px-4 py-4 space-y-3">
        <p className="text-sm text-zinc-400">
          Permanently delete PRD execution <strong className="text-zinc-200">{projectName}</strong> and all its tasks, waves, and execution logs. This cannot be undone.
        </p>
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={`Type "${projectName}" to confirm`}
            className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-red-500 flex-1"
          />
          <button
            onClick={() => void handleDelete()}
            disabled={confirmText !== projectName || deleting}
            className="px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-red-600 hover:bg-red-500 text-white"
          >
            {deleting ? "Deleting..." : "Delete PRD execution"}
          </button>
        </div>
      </div>
    </div>
  );
}
